import { Component, Input, Output, EventEmitter, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  SlurryService,
  RedesignReasonOptionDto,
  SlurryRedesignReasonInputDto,
  SlurryCapabilitiesDto,
  SlurryCapabilityItemDto
} from '../../../../services/slurry.service';

@Component({
  selector: 'app-slurry-redesign-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './slurry-redesign-tab.component.html',
  styleUrls: ['./slurry-redesign-tab.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SlurryRedesignTabComponent implements OnInit {
  @Input() slurryId!: number;
  @Input() projectType = 1;
  @Input() capabilities: SlurryCapabilitiesDto | null = null;
  @Input() isParentProjectCompleted = false;

  /** Emitted after a successful save. Parent should reload capabilities. */
  @Output() saved = new EventEmitter<void>();

  isRedesign = false;
  selectedReasonTypeId: number | null = null;
  selectedComplianceTestIds: number[] = [];
  redesignComments = '';
  redesignReasons: RedesignReasonOptionDto[] = [];
  redesignMessage = '';

  constructor(private readonly slurryService: SlurryService) {}

  ngOnInit(): void {
    this.loadReasons();
    if (this.slurryId > 0) {
      this.loadRedesign();
    }
  }

  private loadReasons(): void {
    this.slurryService.getRedesignReasons(this.projectType).subscribe({
      next: (resp) => { this.redesignReasons = resp?.reasons ?? []; },
      error: (err) => {
        console.error('Failed to load redesign reasons:', err);
        this.redesignReasons = [];
      }
    });
  }

  private loadRedesign(): void {
    this.slurryService.getRedesign(this.slurryId).subscribe({
      next: (r) => {
        if (r) {
          this.isRedesign = true;
          this.selectedReasonTypeId = r.reasonTypeId ?? null;
          this.redesignComments = r.comments ?? '';
          this.selectedComplianceTestIds = r.complianceTestIds ?? [];
        }
      },
      error: (err) => console.error('Failed to load redesign:', err)
    });
  }

  private getCapabilityAllowed(
    selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto,
    fallback: boolean
  ): boolean {
    const item = this.capabilities ? selector(this.capabilities) : null;
    return item?.allowed ?? fallback;
  }

  get selectedReason(): RedesignReasonOptionDto | undefined {
    return this.redesignReasons.find(r => r.reasonTypeId === this.selectedReasonTypeId);
  }

  onReasonChange(): void {
    if (!this.selectedReason?.requiresComplianceTests) {
      this.selectedComplianceTestIds = [];
    }
  }

  toggleComplianceTest(testTypeId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedComplianceTestIds.includes(testTypeId)) {
        this.selectedComplianceTestIds.push(testTypeId);
      }
    } else {
      this.selectedComplianceTestIds = this.selectedComplianceTestIds.filter(id => id !== testTypeId);
    }
  }

  isComplianceTestSelected(testTypeId: number): boolean {
    return this.selectedComplianceTestIds.includes(testTypeId);
  }

  canSaveRedesign(): boolean {
    return this.isRedesign && this.selectedReasonTypeId != null;
  }

  canUpdateRedesign(): boolean {
    const fallback = !this.isParentProjectCompleted;
    return this.getCapabilityAllowed(c => c.updateRedesign, fallback);
  }

  saveRedesign(): void {
    if (!this.slurryId || this.slurryId <= 0 || this.selectedReasonTypeId == null || !this.canUpdateRedesign()) {
      return;
    }
    this.redesignMessage = '';
    const dto: SlurryRedesignReasonInputDto = {
      reasonTypeId: this.selectedReasonTypeId,
      comments: this.redesignComments || undefined,
      complianceTestIds: this.selectedReason?.requiresComplianceTests
        ? this.selectedComplianceTestIds
        : []
    };
    this.slurryService.updateRedesign(this.slurryId, dto).subscribe({
      next: () => {
        this.redesignMessage = 'Redesign saved.';
        this.saved.emit();
      },
      error: (err) => {
        console.error('Failed to save redesign:', err);
        alert('Failed to save redesign.');
      }
    });
  }
}
